import React from 'react';
import { Box, FormField, TextInput } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import type { ProfileFieldConfig } from '../../domain/profile/profile-types';

interface AccountSetupFieldProps {
  field: ProfileFieldConfig;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}

// Not `ProfileFieldInput` (personal-information settings page): its
// `flexBasis` sizing is for a flex-wrap grid, and sets *height* instead of
// width in this screen's single-column vertical StackLayout.
export const AccountSetupField = ({
  field,
  value,
  error,
  onChange,
}: AccountSetupFieldProps) => {
  const { formatMessage } = useIntl();
  const inputId = `account-setup-field-${field.id}`;

  const label = field.optional ? (
    <>
      <Box as="span" css={{ fontWeight: 500 }}>
        {formatMessage({ id: field.labelId })}
      </Box>{' '}
      <Box as="span" css={{ fontWeight: 400 }}>
        {formatMessage({ id: 'STR_OPTIONAL' })}
      </Box>
    </>
  ) : (
    formatMessage({ id: field.labelId })
  );

  return (
    <FormField
      id={inputId}
      label={label}
      inputStatus={error ? 'error' : undefined}
      statusMessage={error ? formatMessage({ id: error }) : undefined}
      css={{
        '& .GDS-input-label': {
          fontSize: '12px',
          lineHeight: '16px',
          letterSpacing: '0px',
          color: '$onSurfaceTextSubdued',
        },
      }}
    >
      <TextInput
        id={inputId}
        type={field.type === 'text' ? 'text' : field.type}
        value={value}
        autoComplete={
          field.autoComplete as React.ComponentProps<
            typeof TextInput
          >['autoComplete']
        }
        readOnly={field.readOnly}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          onChange(event.currentTarget.value)
        }
      />
    </FormField>
  );
};
